import { useCallback, useState } from 'react';
import { connect, type ConnectedProps } from 'react-redux';
import {
  IconClose,
  InformationalScreen,
  LoadingModal,
  Stack,
  Text,
} from '@adc/parallax-component-library';
import { AxiosError } from 'axios';

import {
  Button,
  Footer,
  MainContainer,
  OptInOut,
  RichText,
} from '~/components/utility';
import { ApiStatus, APP_RETURN_TIMEOUT, ErrorCode } from '~/constants';
import { useApiError } from '~/hooks';
import { type RootState } from '~/reducers';
import { AUTH_SESSION } from '~/reducers/auth/types';
import { handleConsent } from '~/services/consent';
import { notifyApp } from '~/utilities/appCommunicator';
import { buildTestId } from '~/utilities/buildTestId';
import i18n from '~/utilities/i18n';
import mediator from '~/utilities/mediator';
import { logout } from '~/utilities/session';

const mapStateToProps = ({
  env: { osType, osVersion, appVersion, deviceId },
  auth,
  nav: { query },
}: RootState) => ({
  deviceId,
  osType,
  osVersion,
  appVersion,
  auth,
  isAccountProfile: !!query.accountProfile,
});

const connector = connect(mapStateToProps);

type Props = ConnectedProps<typeof connector>;

const testID = 'hipaa-consent';

const HipaaConsent = ({
  osType,
  osVersion,
  isAccountProfile,
  appVersion,
  auth,
  deviceId,
  dispatch,
}: Props) => {
  const [loading, setLoading] = useState(false);

  const { showApiErrorModal, showNetworkErrorModal } = useApiError();

  const handleApiRequestError = useCallback(
    (error: AxiosError<ApiErrorData>) => {
      if (error.status === ApiStatus.UNAUTHORIZED) {
        logout();
      } else if (error.code === ErrorCode.NETWORK_ERROR) {
        showNetworkErrorModal();
      } else {
        showApiErrorModal();
      }
    },
    [showApiErrorModal, showNetworkErrorModal],
  );

  const handleConsentWrapper = useCallback(
    async (action: string) => {
      try {
        const resp = await handleConsent({
          dataId: 'hipaa',
          patientId: auth.account_id,
          deviceId,
          action,
          osType,
          osVersion,
          appVersion,
        });
        const updatedAuthSession: AuthSession = {
          ...auth,
          include: {
            patient: {
              ...auth.include?.patient,
              consents: {
                ...auth.include?.patient?.consents,
                hipaa: { ...resp },
              },
            },
          },
        };

        dispatch({ type: AUTH_SESSION, authSession: updatedAuthSession });

        notifyApp('adc-webview:consents', {
          id: 'hipaa',
          action,
          location: isAccountProfile ? 'profile' : 'registration',
        });

        return updatedAuthSession;
      } catch (error) {
        handleApiRequestError(error as AxiosError<ApiErrorData>);
        throw error;
      }
    },
    [
      appVersion,
      auth,
      deviceId,
      handleApiRequestError,
      isAccountProfile,
      osType,
      osVersion,
      dispatch,
    ],
  );

  const handleSubmitHipaaAction = useCallback(
    async (action: string) => {
      try {
        setLoading(true);

        const updatedAuthSession = await handleConsentWrapper(action);

        if (!auth.include?.patient?.consents?.realWorldEvidence?.at) {
          mediator.publish('router:navigate', '/rwe');

          return;
        }

        notifyApp<AuthSession>('adc-webview:signin', updatedAuthSession);

        // This timeout is added to give the app time to redirect the user back.
        setTimeout(() => {
          setLoading(false);
        }, APP_RETURN_TIMEOUT);
      } catch {
        setLoading(false);
      }
    },
    [auth, handleConsentWrapper],
  );

  const onClosePressed = useCallback(() => {
    mediator.publish('router:back');
    notifyApp('adc-webview:navigate-back-app');
  }, []);

  return (
    <MainContainer testID={testID}>
      {loading && (
        <LoadingModal
          aria-label="Loading"
          testID={buildTestId({ testID, suffix: ['loading', 'modal'] })}
        />
      )}
      <InformationalScreen testID={buildTestId({ testID, suffix: 'screen' })}>
        <InformationalScreen.Page
          testID={buildTestId({ testID, suffix: 'page' })}
          topNavigationProps={
            isAccountProfile
              ? {
                  animatedTitle: true,
                  title: i18n.t('Hipaa.title'),
                  buttonStart: {
                    element: (
                      <IconClose
                        testID={buildTestId({
                          testID,
                          suffix: ['close', 'button'],
                        })}
                      />
                    ),
                    label: i18n.t('Hipaa.close'),
                    onPress: onClosePressed,
                  },
                  testID: buildTestId({ testID, suffix: 'nav' }),
                }
              : {
                  title: undefined,
                  testID: buildTestId({ testID, suffix: 'nav' }),
                }
          }
        >
          <InformationalScreen.Content>
            {!isAccountProfile ? (
              <Text
                type="$h1.strong"
                testID={buildTestId({ testID, suffix: 'title' })}
              >
                {i18n.t('Hipaa.title')}
              </Text>
            ) : undefined}
            <RichText
              text={i18n.t('Hipaa.content.hipaaRichText.bodyText')}
              testID={buildTestId({
                testID,
                suffix: ['body', 'text'],
              })}
            />
          </InformationalScreen.Content>
          <InformationalScreen.Footer>
            {!isAccountProfile ? (
              <Footer>
                <Stack alignSelf="flex-end" width="100%" gap="$4">
                  <Button
                    onPress={() => void handleSubmitHipaaAction('accept')}
                    testID={buildTestId({
                      testID,
                      suffix: ['opt', 'in', 'button'],
                    })}
                    aria-label={i18n.t('Global.microcopy.common.accept')}
                    label={i18n.t('Global.microcopy.common.accept')}
                  />
                  <Button
                    variant="tertiary"
                    onPress={() => void handleSubmitHipaaAction('decline')}
                    label={i18n.t('Global.microcopy.common.decline')}
                    aria-label={i18n.t('Global.microcopy.common.decline')}
                    testID={buildTestId({
                      testID,
                      suffix: ['opt', 'out', 'button'],
                    })}
                  />
                </Stack>
              </Footer>
            ) : (
              <OptInOut
                testID={buildTestId({ testID, suffix: ['opt', 'in', 'out'] })}
                optionStrings={{
                  titleTag: 'Global.microcopy.common.status',
                  positiveTag: 'Global.microcopy.common.accepted',
                  negativeTag: 'Global.microcopy.common.declined',
                  positiveOption: 'Global.microcopy.common.optIn',
                  negativeOption: 'Global.microcopy.common.decline',
                }}
                initialState={
                  auth.include?.patient?.consents?.hipaa?.accepted
                    ? 'accept'
                    : 'decline'
                }
                handleConsent={handleConsentWrapper}
              />
            )}
          </InformationalScreen.Footer>
        </InformationalScreen.Page>
      </InformationalScreen>
    </MainContainer>
  );
};

export default connector(HipaaConsent);
